import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings, ChartBar, Heartbeat, PaperPlaneRight } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";
import { formatINR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

const KPIS = [
  { k: "sales_pipeline", l: "Sales Pipeline", c: "#3A86FF" },
  { k: "sales_done", l: "Sales Done", c: "#0D9488" },
  { k: "receivables", l: "Receivables", c: "#F4A261" },
  { k: "inventory_on_hand_value", l: "Inventory On-Hand", c: "#0D9488" },
  { k: "cogs_realised", l: "COGS Realised", c: "#7C3AED" },
  { k: "expenses", l: "Operating Expenses", c: "#D90429" },
  { k: "salaries_paid", l: "Salaries Paid", c: "#7C3AED" },
  { k: "payables", l: "Payables", c: "#D90429" },
  { k: "net_profit_indicative", l: "Net Profit (indicative)", c: "#0A0A0A" },
];

export default function GroupHealth() {
  const [data, setData] = useState({ companies: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("admin");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/insights/group-health");
        setData(data || { companies: [], totals: {} });
        if (isAdmin) {
          const h = await api.get("/reports/business-health").catch(() => null);
          if (h) setHealth(h.data);
        }
      } catch (e) { toast.error(apiErrMsg(e, "Load failed")); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  const sendHealthNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/business-health/send-now");
      if (data.ok) toast.success("Business Health email sent");
      else toast.error(`Send failed: ${data.reason || "unknown"}`);
    } catch (e) { toast.error(apiErrMsg(e, "Send failed")); }
    finally { setSending(false); }
  };

  return (
    <Layout title="Group Business Health" subtitle="All companies · side-by-side comparison + consolidated totals">
      {loading ? (
        <p className="text-sm text-[#717680]">Loading…</p>
      ) : data.companies.length === 0 ? (
        <div className="border border-black/10 bg-white p-16 text-center text-[#717680]">
          <Buildings size={36} className="mx-auto text-[#D90429] mb-3"/>
          <p>No companies configured yet. Add one from Finance → Clients & Vendors.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="group-health-page">
          {/* Admin-only Business Health tiles */}
          {isAdmin && health && (
            <div className="border border-black/10 bg-white" data-testid="business-health-tiles">
              <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heartbeat size={18} weight="fill" className="text-[#D90429]"/>
                  <span className="font-display text-lg font-black tracking-tight">Daily Business Health · {health.date}</span>
                  <span className="text-[10px] font-mono-shelv uppercase text-[#717680] ml-2">Admin only</span>
                </div>
                <button onClick={sendHealthNow} disabled={sending} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="health-send-now">
                  <PaperPlaneRight size={12}/> {sending ? "Sending…" : "Send now"}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5">
                {Object.entries(health.indicators).map(([key, ind]) => (
                  <HealthTile key={key} label={key.replace(/_/g, " ")} indicator={ind}/>
                ))}
              </div>
              {(health.top_debtors?.length > 0 || health.top_creditors?.length > 0 || health.tata_entities?.length > 0 || health.expense_reminders?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-black/10">
                  <MiniList title="Top 5 debtors" rows={health.top_debtors} rowRender={debtorRow} testid="health-debtors"/>
                  <MiniList title="Top 5 creditors" rows={health.top_creditors} rowRender={creditorRow} testid="health-creditors"/>
                </div>
              )}
              {health.expense_reminders?.length > 0 && (
                <div className="border-t border-black/10 p-4 bg-[#F4A261]/10">
                  <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#F4A261] mb-2">⚠ Expense reminders</div>
                  {health.expense_reminders.slice(0, 5).map((r) => (
                    <div key={r.id} className="text-[12px] py-1"><b style={{ color: sevColor(r.severity) }}>{r.label}</b> — {r.header} · {r.vendor || ""} · ₹{Number(r.amount || 0).toLocaleString("en-IN")}</div>
                  ))}
                </div>
              )}
              {health.tata_entities?.length > 0 && (
                <div className="border-t border-black/10 p-4">
                  <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-2">Tata Capital · today&apos;s production</div>
                  {health.tata_entities.map((e, i) => (
                    <div key={i} className="text-[12px] py-1.5 border-b border-black/5 last:border-0" data-testid={`tata-entity-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{tlEmoji(e.traffic_light)}</span>
                        <b>{e.entity}</b>
                        <span className="text-[#717680]">· target {Number(e.daily_target).toLocaleString("en-IN")} · actual {Number(e.actual).toLocaleString("en-IN")} · gap {e.gap_pct}%</span>
                      </div>
                      {e.opinion && <div className="text-[11px] text-[#717680] italic pl-7">{e.opinion}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="border border-black/10 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-[#F5F6F8]">
                <tr>
                  <th className="text-left px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">Metric</th>
                  {data.companies.map((c) => (
                    <th key={c.company_id} className="text-right px-3 py-2 font-display text-sm text-[#0A0A0A]" data-testid={`gh-col-${c.company_id}`}>{c.company_name}</th>
                  ))}
                  <th className="text-right px-3 py-2 font-display text-sm text-[#D90429] bg-[#D90429]/5" data-testid="gh-col-total">GROUP TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {KPIS.map((k) => (
                  <tr key={k.k} className="border-t border-black/5" data-testid={`gh-row-${k.k}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-sm" style={{ color: k.c }}>{k.l}</div>
                    </td>
                    {data.companies.map((c) => (
                      <td key={c.company_id + k.k} className="text-right px-3 py-2.5 font-mono-shelv" style={{ color: k.c }}>{formatINR(c[k.k] || 0)}</td>
                    ))}
                    <td className="text-right px-3 py-2.5 font-mono-shelv font-black bg-[#D90429]/5" style={{ color: k.c }}>{formatINR(data.totals[k.k] || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.companies.map((c) => {
              const npft = Number(c.net_profit_indicative || 0);
              return (
                <div key={c.company_id} className="border border-black/10 bg-white p-5" data-testid={`gh-card-${c.company_id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <ChartBar size={16} weight="bold" className="text-[#D90429]"/>
                    <h3 className="font-display text-xl tracking-tight">{c.company_name}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Metric l="Sales Done" v={c.sales_done} c="#0D9488"/>
                    <Metric l="Receivables" v={c.receivables} c="#F4A261"/>
                    <Metric l="Payables" v={c.payables} c="#D90429"/>
                    <Metric l="Net Profit" v={npft} c={npft >= 0 ? "#0D9488" : "#D90429"}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Metric({ l, v, c }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: c }}>
      <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{l}</div>
      <div className="font-display text-xl font-black" style={{ color: c }}>{formatINR(v)}</div>
    </div>
  );
}


const TL_COLORS = { green: "#0D9488", yellow: "#F4A261", orange: "#EF9748", red: "#D90429" };
const TL_EMOJIS = { green: "🟢", yellow: "🟡", orange: "🟠", red: "🔴" };
function tlEmoji(tl) { return TL_EMOJIS[tl] || "⚪"; }

const debtorRow = (d) => (
  <>
    <span>{d.client}</span>
    <span className="text-right font-bold">₹{Number(d.amount).toLocaleString("en-IN")}</span>
    <span className="text-[10px] text-[#D90429] text-right">{d.overdue_days}d</span>
  </>
);
const creditorRow = (c) => (
  <>
    <span>{c.vendor}</span>
    <span className="text-right font-bold">₹{Number(c.amount).toLocaleString("en-IN")}</span>
    <span className="text-[10px] text-[#D90429] text-right">{c.overdue_days}d</span>
  </>
);

function sevColor(s) { return TL_COLORS[s] || "#717680"; }

function HealthTile({ label, indicator }) {
  const color = TL_COLORS[indicator.traffic_light] || "#717680";
  const val = typeof indicator.value === "number" ? indicator.value.toLocaleString("en-IN") : indicator.value;
  return (
    <div className="p-4 border-r border-b border-black/5 text-center" data-testid={`tile-${label.replace(/ /g, "-")}`}>
      <div className="font-mono-shelv text-[9px] uppercase tracking-widest text-[#717680]">{label}</div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1" style={{ color }}>
        {tlEmoji(indicator.traffic_light)} {val}
      </div>
      <div className="text-[10px] text-[#717680] mt-0.5">{indicator.label}</div>
    </div>
  );
}

function MiniList({ title, rows, rowRender, testid }) {
  return (
    <div className="p-4 border-r border-black/5 last:border-r-0" data-testid={testid}>
      <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-2">{title}</div>
      {rows?.length ? (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 text-[12px] py-1 border-b border-black/5 last:border-0 items-center">
              {rowRender(r)}
            </div>
          ))}
        </div>
      ) : <div className="text-[11px] text-[#717680]">Nothing to show 🎉</div>}
    </div>
  );
}
